import React, { useMemo, useState, useRef, useEffect } from 'react'
import cn from 'classnames'
import {
    Users,
    Mail,
    MessageCircle,
    Star,
    ChevronRight,
    ChevronDown,
    Home,
    LockKeyhole,
    Globe,
    Bell,
    Search,
    Plus,
    Pin,
} from 'lucide-react'
import api from 'api/api'
import useStore from 'state/knovStore'
import useStreamFilters from 'components/filters/useStreamFilters'
import useGetStarredSidebarQuests from 'refactor/hooks/api/useGetStarredSidebarQuests'
import useGetHistory from 'refactor/hooks/api/useGetHistory'
import useGetPeopleChats from 'refactor/hooks/api/useGetPeopleChats'
import TeamIcon from 'components/users/TeamIcon'
import UserIcon from 'components/users/UserIcon'
import StreamsIcon from 'components/shared/icons/StreamsIcon'
import ClipIcon from 'components/shared/icons/ClipIcon'
import useUserHasFeature from 'refactor/hooks/useUserHasFeature'
import { homePrivateEnabled } from 'lib/homePrivate'
import WithTooltip from 'components/shared/WithTooltip'
import useDirectMessageInbox, {
    directMessageLatestMessage,
} from 'refactor/hooks/api/useDirectMessageInbox'
import { directMessageCounterpartId, directMessagesEnabled } from 'lib/directMessages'
import { answerIsNewSinceQuestVisit } from 'lib/questStreamRenderedAnswers'
import { getCachedUser } from 'state/cache'
import styles from 'components/filters/quest-history.module.scss'

// Adapter so the canonical StreamsIcon (the left nav's) fits the generic
// mode-tab render, which passes lucide-style size/className props.
const StreamsTabIcon = ({ size, className }) => (
    <StreamsIcon size={size} contextStyles={styles.streamsTabIcon} iconClassName={className} />
)

const MODES = [
    { key: 'chats', label: 'Chats', Icon: MessageCircle, tip: 'Recent Threads' },
    { key: 'streams', label: 'Channels', Icon: StreamsTabIcon, tip: 'Recent Channels' },
    { key: 'people', label: 'People', Icon: Users, tip: 'Recent People' },
    { key: 'starred', label: 'Starred', Icon: Star, tip: 'Starred Threads' },
]

// Grouped tabs render threads in client-side batches: 5 per group by default,
// with a per-group "Show more" revealing 5 more at a time.
const GROUP_BATCH_INITIAL = 5
const GROUP_BATCH_STEP = 5
const LOAD_MORE_ROOT_MARGIN = 220

const findScrollRoot = node => {
    let root = node?.parentElement
    while (root && root !== document.body) {
        const { overflowY } = getComputedStyle(root)
        const canScroll = root.scrollHeight > root.clientHeight + 1
        if (
            overflowY === 'auto' ||
            overflowY === 'scroll' ||
            (canScroll && overflowY === 'hidden')
        ) {
            return root
        }
        root = root.parentElement
    }
    return null
}

const isNearScrollEnd = root => {
    if (root) {
        return root.scrollHeight - root.scrollTop - root.clientHeight <= LOAD_MORE_ROOT_MARGIN
    }

    const doc = document.documentElement
    const scrollTop = window.pageYOffset || doc.scrollTop || document.body.scrollTop || 0
    return doc.scrollHeight - scrollTop - window.innerHeight <= LOAD_MORE_ROOT_MARGIN
}

// Derive a single-line preview from a quest's content, stripping any markup.
const getPreview = quest => {
    const raw = (quest && (quest.content || quest.title)) || ''
    const text = String(raw)
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    return text || 'Untitled thread'
}

const toTime = value => {
    const d = value ? new Date(value) : null
    return d && !isNaN(d.getTime()) ? d.getTime() : 0
}

const questTime = quest => toTime(quest?.updated_at) || toTime(quest?.created_at)

const questAuthor = quest => quest?.parent?.user || quest?.user || null

// When present, `_actedAt` is when the current user last acted on the thread
// (from the history endpoint) — the meaningful "recency" for these views.
const actedTime = quest => toTime(quest?._actedAt) || questTime(quest)
const displayTime = quest => quest?._actedAt || quest?.updated_at || quest?.created_at

const formatRelativeTime = value => {
    const ts = toTime(value)
    if (!ts) return ''
    const diff = Date.now() - ts
    const minute = 60 * 1000
    const hour = 60 * minute
    const day = 24 * hour
    if (diff < minute) return 'now'
    if (diff < hour) return `${Math.floor(diff / minute)}m`
    if (diff < day) return `${Math.floor(diff / hour)}h`
    if (diff < 7 * day) return `${Math.floor(diff / day)}d`
    const date = new Date(ts)
    const sameYear = date.getFullYear() === new Date().getFullYear()
    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        ...(sameYear ? {} : { year: '2-digit' }),
    })
}

export default function QuestHistory(props) {
    const { panelId, close, append, lightTheme } = props
    const user = gon && gon.currentUser
    const userStreams = useStore(state => state.userStreams)
    const pinnedTeamIds = useStore(state => state.pinnedTeamIds)
    const showClipsInSidebar = useUserHasFeature('show_clips_left_sidebar')
    const filters = useStreamFilters(panelId, { append: !!append })

    const [mode, setMode] = useState('chats')
    const [collapsed, setCollapsed] = useState(() => /** @type {Record<string, boolean>} */ ({}))
    const [pinBusy, setPinBusy] = useState(() => ({}))
    const [starBusy, setStarBusy] = useState(() => ({}))
    const [starredOverrides, setStarredOverrides] = useState(() => ({}))
    const starredQuestIds = useStore(state => state.starredQuestIds)

    // What the user is looking at (center panel's filter) — the matching
    // chat/stream row keeps the nav-style selected highlight. centerIndex is
    // synced by PanelController on opens and swipes, so this tracks both.
    const centerFilter = useStore(state => state.panels.state?.[state.centerIndex]?.filter || null)
    const selectedQuestId = centerFilter?.questId || null
    // A stream is "where you are" when its feed itself is centered (not a
    // thread within it — that highlights in Chats instead).
    const selectedStreamId = selectedQuestId ? null : centerFilter?.team_ids?.[0] || null

    // Reveal the quick-nav row only once the header is pinned (i.e. the main
    // sidebar nav above has scrolled off). A zero-height sentinel at the top of
    // the panel drives it: the header is "stuck" once the sentinel scrolls above
    // the sidebar scroll port's top edge.
    const sentinelRef = useRef(null)
    const [navPinned, setNavPinned] = useState(false)
    useEffect(() => {
        const sentinel = sentinelRef.current
        if (!sentinel) return
        let root = sentinel.parentElement
        while (root && root !== document.body) {
            const oy = getComputedStyle(root).overflowY
            if (oy === 'auto' || oy === 'scroll' || oy === 'hidden') break
            root = root.parentElement
        }
        const scroller = root && root !== document.body ? root : window
        const check = () => {
            const topEdge = scroller === window ? 0 : scroller.getBoundingClientRect().top
            setNavPinned(sentinel.getBoundingClientRect().top < topEdge - 1)
        }
        check()
        scroller.addEventListener('scroll', check, { passive: true })
        window.addEventListener('resize', check)
        return () => {
            scroller.removeEventListener('scroll', check)
            window.removeEventListener('resize', check)
        }
    }, [])

    // Data sources by mode:
    //  - Streams / Chats → action history (`/history`, one row per quest, recency-first)
    //  - Starred         → slim starred-thread previews from the quests endpoint
    //  - People          → `/people` (latest thread shared with each other person)
    const starredMode = mode === 'starred'
    const peopleMode = mode === 'people'
    const historyMode = !starredMode && !peopleMode
    // useGetHistory returns a loose union ({actions} when disabled); cast for the
    // infinite-query fields it also exposes when enabled. Chats uses the
    // participated feed (threads you're in, ordered by their latest answer);
    // Streams keeps the plain view-history for its activity previews.
    const history = /** @type {any} */ (
        useGetHistory(!!user && historyMode, { participated: mode === 'chats' })
    )
    const starred = useGetStarredSidebarQuests(!!user && starredMode)
    const peopleChats = useGetPeopleChats(!!user && peopleMode)
    const autoLoadMoreRef = useRef(null)
    const autoFetchInFlightRef = useRef(false)

    // Direct messages never reach the search index the participated history
    // reads, so Chats folds the inbox in, each conversation at its latest message.
    const directMessageInbox = useDirectMessageInbox(
        !!user && mode === 'chats' && directMessagesEnabled(),
    )

    // History actions carry the quest plus the time the current user acted on it.
    const quests = useMemo(() => {
        if (starredMode) return starred.quests || []
        if (peopleMode) return []
        const historyQuests = (history.actions || [])
            .filter(action => action && action.quest)
            .map(action => ({
                ...action.quest,
                _actedAt: action.created_at,
                _lastAnswer: action.answer || null,
            }))
        if (mode !== 'chats') return historyQuests
        const directMessages = directMessageInbox.quests.map(quest => {
            const latest = directMessageLatestMessage(quest)
            return {
                ...quest,
                _actedAt: latest?.created_at || quest.updated_at,
                _directMessage: { latest },
            }
        })
        return [...historyQuests, ...directMessages]
    }, [starredMode, peopleMode, mode, starred.quests, history.actions, directMessageInbox.quests])
    const starredQuests = useMemo(
        () =>
            starredMode
                ? (quests || []).filter(
                      quest => quest && starredOverrides[String(quest.id)] !== false,
                  )
                : [],
        [starredMode, quests, starredOverrides],
    )

    const isFetching = starredMode
        ? starred.isFetching
        : peopleMode
        ? peopleChats.isFetching
        : history.isFetching
    const hasNextPage = starredMode
        ? starred.hasNextPage
        : peopleMode
        ? peopleChats.hasNextPage
        : history.hasNextPage
    const fetchNextPage = starredMode
        ? starred.fetchNextPage
        : peopleMode
        ? peopleChats.fetchNextPage
        : history.fetchNextPage
    const isFetchingNextPage = starredMode ? starred.isFetchingNextPage : false

    const autoLoadMode = starredMode || mode === 'chats'
    useEffect(() => {
        const target = autoLoadMoreRef.current
        if (!autoLoadMode || !hasNextPage || !target || !fetchNextPage) return

        const root = findScrollRoot(target)
        const scrollTarget = root || window
        const fetchMore = () => {
            if (!hasNextPage || isFetching || autoFetchInFlightRef.current) return

            autoFetchInFlightRef.current = true
            Promise.resolve(fetchNextPage()).finally(() => {
                autoFetchInFlightRef.current = false
            })
        }
        const checkNearEnd = () => {
            if (isNearScrollEnd(root)) fetchMore()
        }
        const observer = new IntersectionObserver(
            entries => {
                if (!entries.some(entry => entry.isIntersecting)) return
                fetchMore()
            },
            {
                root,
                rootMargin: `${LOAD_MORE_ROOT_MARGIN}px 0px`,
            },
        )
        observer.observe(target)
        scrollTarget.addEventListener('scroll', checkNearEnd, { passive: true })
        window.addEventListener('resize', checkNearEnd)
        checkNearEnd()

        return () => {
            observer.disconnect()
            scrollTarget.removeEventListener('scroll', checkNearEnd)
            window.removeEventListener('resize', checkNearEnd)
        }
    }, [autoLoadMode, hasNextPage, fetchNextPage, isFetching, starredQuests.length, quests.length])

    const streamsById = useMemo(() => {
        const map = {}
        ;(userStreams || []).forEach(stream => {
            map[stream.id] = stream
        })
        return map
    }, [userStreams])

    // Resolve the display author for a thread, falling back to a participant when
    // older payloads do not include the parent answer user.
    const resolvePerson = quest => {
        const author = questAuthor(quest)
        if (author) return { person: author, label: author.name || 'Unknown' }

        const participants = /** @type {any} */ (quest).participants || []
        const others = participants.filter(p => p && p.id && p.id !== user?.id)
        const person = others[0] || user
        const isSelf = person?.id && person.id === user?.id
        return { person, label: isSelf ? 'You' : person?.name || 'Unknown' }
    }

    // The /people endpoint tells us which person each thread belongs to; resolve that
    // participant off any of their threads, falling back to a thin user so the
    // avatar still hydrates.
    const resolvePersonById = (quests, userId) => {
        for (const quest of quests || []) {
            const participants = /** @type {any} */ (quest)?.participants || []
            const person = participants.find(p => p && String(p.id) === String(userId))
            if (person) return person
        }
        return { id: userId }
    }

    const groups = useMemo(() => {
        if (mode === 'people') {
            // Already recency-ordered by the server (people and threads within
            // each). Rows represent threads, so they show the thread title and
            // open the thread top. Answer metadata still supplies interaction
            // recency and fallback participant identity.
            return (peopleChats.people || [])
                .filter(entry => entry && entry.chats?.length)
                .map(entry => {
                    const quests = entry.chats.map(chat => ({
                        ...chat.quest,
                        _actedAt: chat.created_at,
                        _lastAnswer: chat.answer || null,
                    }))
                    let person = entry.user
                    if (!person?.name) {
                        const resolvedPerson = resolvePersonById(quests, entry.user_id)
                        const answerUser = entry.chats[0]?.answer?.user
                        const answerUserMatchesEntry =
                            answerUser && String(answerUser.id) === String(entry.user_id)
                        person = resolvedPerson?.name
                            ? resolvedPerson
                            : answerUserMatchesEntry
                            ? answerUser
                            : resolvedPerson
                    }
                    return {
                        key: `user-${entry.user_id}`,
                        kind: 'person',
                        person,
                        label: person?.name || 'Unknown',
                        count: entry.count,
                        quests,
                    }
                })
        }

        return []
    }, [mode, peopleChats.people])

    // Flat, most-recent-first list for the Chats view. Each entry shows the stream
    // on top and the author + content beneath.
    const chats = useMemo(() => {
        if (mode !== 'chats') return []
        return (quests || [])
            .filter(Boolean)
            .slice()
            .sort((a, b) => actedTime(b) - actedTime(a))
            .map(quest => {
                if (quest._directMessage) {
                    // A direct message is the other person: their face and name
                    // head the row, the latest message sits under it.
                    const counterpartId = directMessageCounterpartId(quest, user?.id)
                    const counterpart = (quest.participants || []).find(
                        p => p && String(p.id) === String(counterpartId),
                    ) ||
                        getCachedUser(counterpartId) || { id: counterpartId }
                    const latest = quest._directMessage.latest
                    const mine =
                        latest && String(latest.user_id ?? latest.user?.id) === String(user?.id)
                    const text = latest
                        ? String(latest.content || '')
                              .replace(/<[^>]*>/g, ' ')
                              .replace(/\s+/g, ' ')
                              .trim() || 'Sent an attachment'
                        : null
                    return {
                        quest,
                        rootAuthor: counterpart,
                        stream: null,
                        isSystemStream: true,
                        isPublic: false,
                        directMessage: true,
                        title: counterpart?.name || counterpart?.username || 'Direct message',
                        reply: text
                            ? {
                                  id: latest.id,
                                  text,
                                  user: latest.user,
                                  name: mine ? 'you' : null,
                                  more: 0,
                              }
                            : null,
                        unseen: !!latest && !mine && answerIsNewSinceQuestVisit(latest, quest),
                        answerId: latest?.id || null,
                        streamLabel: 'Direct message',
                    }
                }
                const streamId = quest.team_id
                const stream = streamId ? streamsById[streamId] || quest.team : null
                // WhatsApp grain: the row is the thread's latest answer — its
                // author fronts the row and its text is the preview. Fall back
                // to the thread root when no answer came through.
                const lastAnswer = quest._lastAnswer
                const answerAuthor = lastAnswer?.user
                const isSelf = answerAuthor?.id && String(answerAuthor.id) === String(user?.id)
                const identity = answerAuthor
                    ? {
                          person: answerAuthor,
                          label: isSelf ? 'You' : answerAuthor.name || 'Unknown',
                      }
                    : resolvePerson(quest)
                // The row is a place: the thread (root author's avatar + title)
                // with its reply line beneath. Unseen threads show the newest
                // answer by someone else since your last interaction, bright,
                // with a "+N more" count — clicking lands at the OLDEST unseen
                // (resume_id) so you read forward. Seen threads show their
                // latest answer as quiet context (row dims, no count) and
                // clicking lands on it. Suppress the line when it would just
                // echo the root (e.g. a root-only thread) — unless more unseen
                // sit behind it, where dropping it would lose the +N count and
                // the resume landing.
                const title = getPreview(quest)
                const resumeText = lastAnswer?.text
                    ? String(lastAnswer.text).replace(/\s+/g, ' ').trim()
                    : null
                const unseenCount = lastAnswer?.unseen_count ?? 1
                const replyAuthor = lastAnswer?.user
                const replyIsMine = replyAuthor?.id && String(replyAuthor.id) === String(user?.id)
                const reply =
                    resumeText && !(unseenCount <= 1 && resumeText === title)
                        ? {
                              id: lastAnswer.resume_id || lastAnswer.id,
                              text: resumeText,
                              user: replyAuthor,
                              name: replyIsMine ? 'you' : replyAuthor?.name || null,
                              more: Math.max(0, unseenCount - 1),
                          }
                        : null
                return {
                    quest,
                    rootAuthor: questAuthor(quest) || identity.person,
                    stream,
                    isSystemStream: !streamId,
                    // The perms icon reflects the stream's visibility: for a team
                    // stream that's the stream's own public flag, for a system
                    // stream it's the thread's.
                    isPublic: !!(stream ? stream.public : quest.public),
                    title,
                    reply,
                    unseen: unseenCount > 0,
                    answerId: reply?.id || null,
                    streamLabel: stream?.name || (quest.public ? 'Public' : 'Private'),
                }
            })
    }, [quests, mode, streamsById])

    // Streams tab: container grain — one row per stream you're a member of.
    // Latest-activity previews hydrate opportunistically from the history feed
    // (already fetched for this mode); pinned streams first, then most recently
    // active, then name.
    const streamDirectory = useMemo(() => {
        if (mode !== 'streams') return []
        const latestByTeam = {}
        // History is recency-first, so the first hit per stream is its latest.
        ;(quests || []).forEach(quest => {
            const teamId = quest?.team_id
            if (teamId && !latestByTeam[teamId]) latestByTeam[teamId] = quest
        })
        const rows = (userStreams || []).filter(Boolean).map(stream => ({
            stream,
            latest: latestByTeam[stream.id] || null,
            pinned: !!pinnedTeamIds?.[stream.id],
        }))
        rows.sort((a, b) => {
            if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
            const at = a.latest ? actedTime(a.latest) : 0
            const bt = b.latest ? actedTime(b.latest) : 0
            if (at !== bt) return bt - at
            return (a.stream.name || '').localeCompare(b.stream.name || '')
        })
        return rows
    }, [mode, quests, userStreams, pinnedTeamIds])
    const pinnedStreamRows = streamDirectory.filter(row => row.pinned)
    const recentStreamRows = streamDirectory.filter(row => !row.pinned)

    // With an answerId, the panel opens the answer's permalink (scrolled to and
    // highlighted); without one it opens the thread top.
    const openQuest = (quest, answerId = null) => {
        filters.selectNewFilter({ questId: quest.id, ...(answerId ? { answerId } : {}) })
        close && close()
    }

    const openQuestFromKeyboard = (ev, quest, answerId = null) => {
        if (ev.key !== 'Enter' && ev.key !== ' ') return
        ev.preventDefault()
        openQuest(quest, answerId)
    }

    const toggleStarredQuest = async (ev, quest) => {
        ev.preventDefault()
        ev.stopPropagation()
        if (!quest?.id || starBusy[quest.id]) return

        const questId = String(quest.id)
        const currentlyStarred = starredOverrides[questId] ?? starredQuestIds?.[questId] ?? true
        setStarBusy(prev => ({ ...prev, [questId]: true }))
        setStarredOverrides(prev => ({ ...prev, [questId]: !currentlyStarred }))
        useStore.getState().set(draft => {
            if (currentlyStarred) delete draft.starredQuestIds[questId]
            else draft.starredQuestIds[questId] = true
        })

        try {
            const res = currentlyStarred
                ? await api.unstarQuest(questId)
                : await api.starQuest(questId)
            if (!res?.ok) throw new Error('Failed to update starred thread')
            starred.refetch?.()
        } catch (error) {
            console.warn('Failed to toggle starred thread from history', error)
            setStarredOverrides(prev => {
                const next = { ...prev }
                delete next[questId]
                return next
            })
            useStore.getState().set(draft => {
                if (currentlyStarred) draft.starredQuestIds[questId] = true
                else delete draft.starredQuestIds[questId]
            })
        } finally {
            setStarBusy(prev => {
                const next = { ...prev }
                delete next[questId]
                return next
            })
        }
    }

    const renderStarToggle = quest => {
        const questId = String(quest?.id || '')
        const isStarred = starredOverrides[questId] ?? starredQuestIds?.[questId] ?? true
        const label = isStarred ? 'Unstar thread' : 'Star thread'

        return (
            <button
                type="button"
                className={cn(styles.starToggle, isStarred && styles.starToggleActive)}
                aria-label={label}
                aria-pressed={!!isStarred}
                title={label}
                disabled={!!starBusy[questId]}
                onClick={ev => toggleStarredQuest(ev, quest)}
                onKeyDown={ev => ev.stopPropagation()}
            >
                <Star size={14} fill={isStarred ? 'currentColor' : 'none'} />
            </button>
        )
    }

    const renderQuestRow = (quest, peopleRow = false, options = {}) => {
        const lastAnswer = quest._lastAnswer
        const identity = peopleRow ? null : resolvePerson(quest)
        const preview = getPreview(quest)
        const answerId = peopleRow ? null : lastAnswer?.id || null
        const rowContent = (
            <>
                {identity && (
                    <UserIcon
                        user={identity.person}
                        size="small"
                        contextStyles={styles.questAvatar}
                    />
                )}
                <span className={styles.questLine}>
                    {identity && <span className={styles.questAuthor}>{identity.label}</span>}
                    <span className={styles.questText}>{preview}</span>
                </span>
                <span className={styles.questTime}>{formatRelativeTime(displayTime(quest))}</span>
            </>
        )

        if (options.starredControl) {
            return (
                <li key={quest.id} className={styles.questItem}>
                    <div className={styles.questRowWithAction}>
                        <button
                            type="button"
                            className={cn(styles.questRow, styles.questRowActionTarget)}
                            onClick={() => openQuest(quest, answerId)}
                        >
                            {rowContent}
                        </button>
                        {renderStarToggle(quest)}
                    </div>
                </li>
            )
        }

        return (
            <li key={quest.id} className={styles.questItem}>
                <div
                    role="button"
                    tabIndex={0}
                    className={styles.questRow}
                    onClick={() => openQuest(quest, answerId)}
                    onKeyDown={ev => openQuestFromKeyboard(ev, quest, answerId)}
                >
                    {rowContent}
                </div>
            </li>
        )
    }

    const refreshStreams = async () => {
        const { activeSpaceId } = useStore.getState()
        const teams = await api.getTeams({ space_id: activeSpaceId })
        useStore.getState().set({ userStreams: teams })
    }

    const toggleStreamPin = async (ev, stream) => {
        ev.preventDefault()
        ev.stopPropagation()
        if (!stream?.id || pinBusy[stream.id]) return

        const streamId = stream.id
        const wasPinned = !!pinnedTeamIds?.[streamId]
        setPinBusy(prev => ({ ...prev, [streamId]: true }))
        useStore.getState().set(draft => {
            if (wasPinned) delete draft.pinnedTeamIds[streamId]
            else draft.pinnedTeamIds[streamId] = true
        })
        try {
            if (wasPinned) await api.unpinTeam(streamId)
            else await api.pinTeam(streamId)
        } catch (error) {
            console.warn('Failed to toggle sidebar stream pin', error)
            useStore.getState().set(draft => {
                if (wasPinned) draft.pinnedTeamIds[streamId] = true
                else delete draft.pinnedTeamIds[streamId]
            })
            return
        } finally {
            setPinBusy(prev => {
                const next = { ...prev }
                delete next[streamId]
                return next
            })
        }
        refreshStreams().catch(error => {
            console.warn('Failed to refresh streams after sidebar pin toggle', error)
        })
    }

    const renderStreamPinToggle = stream => {
        if (!stream?.id) return null

        const pinned = !!pinnedTeamIds?.[stream.id]
        const label = pinned ? 'Unpin channel' : 'Pin channel'

        return (
            <button
                type="button"
                className={cn(styles.pinToggle, pinned && styles.pinToggleActive)}
                aria-label={label}
                aria-pressed={pinned}
                title={label}
                onClick={ev => toggleStreamPin(ev, stream)}
                onKeyDown={ev => ev.stopPropagation()}
                disabled={!!pinBusy[stream.id]}
            >
                <Pin size={12} />
            </button>
        )
    }

    // Chats rows: the stream label is a click-through — real streams open the
    // stream, system labels open the Private/Public filter.
    const openChatStream = (ev, stream, isPublic) => {
        ev.stopPropagation()
        if (stream?.id) filters.selectTeam(stream.id, {})
        else if (isPublic) filters.selectPublic()
        else filters.selectPrivate()
        close && close()
    }

    const openStreamFromKeyboard = (ev, stream) => {
        if (ev.key !== 'Enter' && ev.key !== ' ') return
        ev.preventDefault()
        openChatStream(ev, stream, false)
    }

    const renderStreamDirRow = ({ stream, latest }) => (
        <li key={stream.id}>
            <div
                role="button"
                tabIndex={0}
                className={cn(
                    styles.streamRow,
                    selectedStreamId &&
                        String(stream.id) === String(selectedStreamId) &&
                        styles.chatRowSelected,
                )}
                onClick={ev => openChatStream(ev, stream, false)}
                onKeyDown={ev => openStreamFromKeyboard(ev, stream)}
            >
                <TeamIcon team={stream} tip={stream.name} contextStyles={styles.streamDirLogo} />
                <span className={styles.streamDirLine}>
                    <span className={styles.streamDirName}>{stream.name}</span>
                    {latest && (
                        <span className={styles.streamDirPreview}>
                            {resolvePerson(latest).label}: {getPreview(latest)}
                        </span>
                    )}
                </span>
                <span className={styles.streamRowMeta}>
                    {renderStreamPinToggle(stream)}
                    {latest && (
                        <span className={styles.chatTime}>
                            {formatRelativeTime(displayTime(latest))}
                        </span>
                    )}
                </span>
            </div>
        </li>
    )

    // Quick-access system streams, pinned above the mode tabs (mirrors the main
    // sidebar nav). Home honors the saved defaultHomeStream preference.
    const goHome = () => {
        const preferFollowing = user?.space_options?.defaultHomeStream === 'following'
        preferFollowing ? filters.selectFollowing() : filters.selectPublic()
        close && close()
    }
    const goPrivate = () => {
        filters.selectPrivate()
        close && close()
    }
    const goNotifications = () => {
        filters.selectNotifications()
        close && close()
    }
    const goStreams = () => {
        filters.selectStreams()
        close && close()
    }
    const goClips = () => {
        filters.selectClips()
        close && close()
    }
    const goSearch = () => {
        filters.selectSearch()
        close && close()
    }

    const toggleGroup = key => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }))

    const itemCount =
        mode === 'chats'
            ? chats.length
            : mode === 'streams'
            ? streamDirectory.length
            : mode === 'starred'
            ? starredQuests.length
            : groups.length

    // Per-group batching: each stream/person group shows a batch of threads with
    // its own "Show more" to expand that group independently. Reset on tab switch.
    const [groupLimits, setGroupLimits] = useState({})
    useEffect(() => setGroupLimits({}), [mode])
    const expandGroup = key =>
        setGroupLimits(prev => ({
            ...prev,
            [key]: (prev[key] ?? GROUP_BATCH_INITIAL) + GROUP_BATCH_STEP,
        }))

    return (
        <div className={cn(styles.questHistory, lightTheme && styles.light)}>
            <div ref={sentinelRef} className={styles.stickySentinel} aria-hidden="true" />
            <div className={styles.stickyHeader}>
                {navPinned && (
                    <div className={styles.streamNavRow}>
                        <button
                            type="button"
                            className={styles.streamNavButton}
                            title="Home"
                            aria-label="Home"
                            onClick={goHome}
                        >
                            <Home size={18} />
                        </button>
                        {!homePrivateEnabled() && (
                            <button
                                type="button"
                                className={styles.streamNavButton}
                                title="Private"
                                aria-label="Private"
                                onClick={goPrivate}
                            >
                                <LockKeyhole size={18} />
                            </button>
                        )}
                        <button
                            type="button"
                            className={styles.streamNavButton}
                            title="Notifications"
                            aria-label="Notifications"
                            onClick={goNotifications}
                        >
                            <Bell size={18} />
                        </button>
                        <button
                            type="button"
                            className={styles.streamNavButton}
                            title="Channels"
                            aria-label="Channels"
                            onClick={goStreams}
                        >
                            <StreamsIcon size={18} contextStyles={styles.streamsTabIcon} />
                        </button>
                        <button
                            type="button"
                            className={styles.streamNavButton}
                            title="Search"
                            aria-label="Search"
                            onClick={goSearch}
                        >
                            <Search size={18} />
                        </button>
                        {showClipsInSidebar && (
                            <button
                                type="button"
                                className={styles.streamNavButton}
                                title="Clips"
                                aria-label="Clips"
                                onClick={goClips}
                            >
                                <ClipIcon contextStyles={styles.quickNavClipIcon} />
                            </button>
                        )}
                    </div>
                )}
                <div className={styles.modeRow} role="tablist" aria-label="History view">
                    {MODES.map(({ key, label, Icon, tip }) => (
                        <WithTooltip key={key} tip={tip} asChild>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={mode === key}
                                aria-label={tip}
                                className={cn(
                                    styles.modeButton,
                                    mode === key && styles.modeButtonActive,
                                )}
                                onClick={() => setMode(key)}
                            >
                                <Icon size={18} className={styles.modeIcon} />
                                <span className={styles.modeLabel}>{label}</span>
                            </button>
                        </WithTooltip>
                    ))}
                </div>
            </div>

            <div className={styles.groups}>
                {starredMode && starredQuests.length > 0 && (
                    <div className={styles.sectionHeader}>Starred Threads</div>
                )}
                {starredMode && (
                    <ul className={styles.questList} aria-label="Starred threads">
                        {starredQuests.map(quest =>
                            renderQuestRow(quest, false, { starredControl: true }),
                        )}
                    </ul>
                )}

                {peopleMode && groups.length > 0 && (
                    <div className={styles.sectionHeader}>Recent People</div>
                )}
                {groups.map(group => {
                    const isCollapsed = !!collapsed[group.key]
                    const count = group.count ?? group.quests.length
                    const limit = groupLimits[group.key] ?? GROUP_BATCH_INITIAL
                    const shownQuests = group.quests.slice(0, limit)
                    const groupHasMore = group.quests.length > shownQuests.length
                    return (
                        <div key={group.key} className={styles.group}>
                            <div className={styles.groupHeader}>
                                <button
                                    type="button"
                                    className={styles.disclosure}
                                    aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                                    aria-expanded={!isCollapsed}
                                    onClick={() => toggleGroup(group.key)}
                                >
                                    {isCollapsed ? (
                                        <ChevronRight size={15} />
                                    ) : (
                                        <ChevronDown size={15} />
                                    )}
                                </button>

                                <button
                                    type="button"
                                    className={styles.groupTitle}
                                    onClick={() => toggleGroup(group.key)}
                                >
                                    <span className={styles.groupAvatar}>
                                        <UserIcon
                                            user={group.person}
                                            size="small"
                                            contextStyles={styles.personAvatar}
                                        />
                                    </span>
                                    <span className={styles.groupLabel}>{group.label}</span>
                                    <span className={styles.groupCount}>{count}</span>
                                </button>
                            </div>

                            {!isCollapsed && (
                                <div className={styles.groupBody}>
                                    <ul className={styles.questList}>
                                        {shownQuests.map(quest => renderQuestRow(quest, true))}
                                    </ul>
                                    {groupHasMore && (
                                        <button
                                            type="button"
                                            className={styles.groupShowMore}
                                            onClick={() => {
                                                expandGroup(group.key)
                                                // Top up from the server when this
                                                // group's loaded threads run low.
                                                if (
                                                    !peopleMode &&
                                                    hasNextPage &&
                                                    !isFetching &&
                                                    limit + GROUP_BATCH_STEP >= group.quests.length
                                                ) {
                                                    fetchNextPage()
                                                }
                                            }}
                                        >
                                            Show{' '}
                                            {Math.min(
                                                group.quests.length - shownQuests.length,
                                                GROUP_BATCH_STEP,
                                            )}{' '}
                                            more
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )
                })}

                {starredMode && itemCount > 0 && (hasNextPage || isFetchingNextPage) && (
                    <div
                        ref={autoLoadMoreRef}
                        className={cn(
                            styles.loadMoreSentinel,
                            isFetchingNextPage && styles.loadMoreSentinelLoading,
                        )}
                        aria-hidden={!isFetchingNextPage}
                    >
                        {isFetchingNextPage && (
                            <span className={styles.loadMoreStatus}>
                                <i className="fa fa-circle-o-notch fa-spin" />
                                Loading...
                            </span>
                        )}
                    </div>
                )}

                {mode === 'streams' && (
                    <>
                        {/* The directory is pinned-first, so it splits cleanly into
                            two captioned sections; the pinned one only appears once
                            something is pinned. */}
                        {pinnedStreamRows.length > 0 && (
                            <>
                                <div className={styles.sectionHeader}>Pinned Channels</div>
                                <ul className={styles.chatList}>
                                    {pinnedStreamRows.map(renderStreamDirRow)}
                                </ul>
                            </>
                        )}
                        {recentStreamRows.length > 0 && (
                            <div className={styles.sectionHeader}>Recent Channels</div>
                        )}
                        <ul className={styles.chatList}>
                            {recentStreamRows.map(renderStreamDirRow)}
                            <li key="__discover__">
                                <button
                                    type="button"
                                    className={styles.streamRow}
                                    onClick={goStreams}
                                >
                                    <span className={styles.fallbackAvatar}>
                                        <Plus size={14} />
                                    </span>
                                    <span className={styles.streamDirDiscover}>
                                        Discover channels
                                    </span>
                                </button>
                            </li>
                        </ul>
                    </>
                )}

                {mode === 'chats' && chats.length > 0 && (
                    <div className={styles.sectionHeader}>Recent Threads</div>
                )}
                {mode === 'chats' && (
                    <ul className={styles.chatList}>
                        {chats.map(
                            ({
                                quest,
                                rootAuthor,
                                isPublic,
                                isSystemStream,
                                directMessage,
                                title,
                                reply,
                                unseen,
                                answerId,
                                streamLabel,
                            }) => {
                                const timeLabel = formatRelativeTime(displayTime(quest))
                                const streamIcon = directMessage ? (
                                    <Mail size={11} />
                                ) : isPublic ? (
                                    <Globe size={11} />
                                ) : (
                                    <LockKeyhole size={11} />
                                )
                                // The title line whispers who started the thread, in
                                // the same muted name → text rhythm as the reply row.
                                const rootIsMine =
                                    rootAuthor?.id && String(rootAuthor.id) === String(user?.id)
                                const titleAuthorName =
                                    !directMessage && rootAuthor
                                        ? rootIsMine
                                            ? 'you'
                                            : rootAuthor.name || null
                                        : null
                                // The reply's inner content is shared: team streams
                                // hang it on a stem below the avatar, simple streams
                                // stack it as a second line inside the avatar column.
                                const replyInner = reply && (
                                    <>
                                        {reply.name && (
                                            <span className={styles.chatReplyName}>
                                                {reply.name}
                                            </span>
                                        )}
                                        <span className={styles.chatReplyText}>{reply.text}</span>
                                        {reply.more > 0 && (
                                            <span className={styles.chatReplyMore}>
                                                {reply.more > 99
                                                    ? '99+ more'
                                                    : `+${reply.more} more`}
                                            </span>
                                        )}
                                    </>
                                )

                                return (
                                    <li key={quest.id} className={styles.chatItem}>
                                        <button
                                            type="button"
                                            className={cn(
                                                styles.chatRow,
                                                unseen
                                                    ? styles.chatRowUnseen
                                                    : styles.chatRowRested,
                                                selectedQuestId &&
                                                    String(quest.id) === String(selectedQuestId) &&
                                                    styles.chatRowSelected,
                                            )}
                                            onClick={() => openQuest(quest, answerId)}
                                        >
                                            <span className={styles.chatHead}>
                                                <span className={styles.chatAvatar}>
                                                    {/* The perms icon rides a reserved rail
                                                        left of the avatar, anchored to the
                                                        avatar face so it sits at the same
                                                        spot on every row regardless of the
                                                        row's height. */}
                                                    <span className={styles.chatKindIcon}>
                                                        {streamIcon}
                                                    </span>
                                                    <UserIcon
                                                        user={rootAuthor}
                                                        size="small"
                                                        contextStyles={styles.chatAvatarImg}
                                                    />
                                                </span>
                                                <span className={styles.chatHeadCol}>
                                                    {!isSystemStream && (
                                                        <span className={styles.chatMeta}>
                                                            <span className={styles.chatStream}>
                                                                {streamLabel}
                                                            </span>
                                                            <span className={styles.chatTime}>
                                                                {timeLabel}
                                                            </span>
                                                        </span>
                                                    )}
                                                    <span className={styles.chatTitleLine}>
                                                        {titleAuthorName && (
                                                            <span
                                                                className={styles.chatTitleAuthor}
                                                            >
                                                                {titleAuthorName}
                                                            </span>
                                                        )}
                                                        <span className={styles.chatTitle}>
                                                            {title}
                                                        </span>
                                                        {isSystemStream && (
                                                            <span className={styles.chatCornerMeta}>
                                                                <span className={styles.chatTime}>
                                                                    {timeLabel}
                                                                </span>
                                                            </span>
                                                        )}
                                                    </span>
                                                    {isSystemStream && reply && (
                                                        <span className={styles.chatReplyLine}>
                                                            {replyInner}
                                                        </span>
                                                    )}
                                                </span>
                                            </span>
                                            {!isSystemStream && reply && (
                                                <span className={styles.chatReplyRow}>
                                                    <span
                                                        className={styles.chatStem}
                                                        aria-hidden="true"
                                                    />
                                                    {replyInner}
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                )
                            },
                        )}
                    </ul>
                )}

                {/* Also with nothing shown yet: a page can filter down to
                    nothing (the radio station chat) while later ones have chats. */}
                {mode === 'chats' && hasNextPage && (
                    <div
                        ref={autoLoadMoreRef}
                        className={styles.loadMoreSentinel}
                        aria-hidden="true"
                    />
                )}

                {!isFetching && itemCount === 0 && !(mode === 'chats' && hasNextPage) && (
                    <div className={styles.emptyState}>
                        {mode === 'starred'
                            ? 'No starred threads yet.'
                            : mode === 'chats'
                            ? 'No chats yet.'
                            : mode === 'people'
                            ? 'No conversations yet.'
                            : mode === 'streams'
                            ? 'No channels yet — discover one below.'
                            : 'No history yet — start a thread.'}
                    </div>
                )}

                {isFetching && itemCount === 0 && (
                    <div className={styles.loadingState}>
                        <span className={styles.skeletonRow} />
                        <span className={styles.skeletonRow} />
                        <span className={styles.skeletonRow} />
                    </div>
                )}

                {mode === 'people' && hasNextPage && itemCount > 0 && (
                    <button
                        type="button"
                        className={styles.showMore}
                        onClick={() => fetchNextPage()}
                        disabled={isFetching}
                    >
                        {isFetching ? 'Loading…' : 'Show more'}
                    </button>
                )}
            </div>
        </div>
    )
}
